import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { saveConversationUI } from "../services/conversationUIStorage";
const ConversationContext = createContext(undefined);
export const ConversationProvider = ({ children }) => {
    const [state, setState] = useState({
        conversationId: null,
        messages: [],
        isLoading: false,
        error: null,
    });
    const setConversationId = useCallback((id) => {
        setState((prev) => ({ ...prev, conversationId: id }));
    }, []);
    const addMessage = useCallback((message) => {
        setState((prev) => {
            // If message has an ID, check if it already exists
            if (message.id) {
                const existingIndex = prev.messages.findIndex(m => m.id === message.id);
                if (existingIndex !== -1) {
                    // Update existing message - merge toolCall data to preserve params
                    const updatedMessages = [...prev.messages];
                    const existingMessage = updatedMessages[existingIndex];
                    updatedMessages[existingIndex] = {
                        ...existingMessage,
                        ...message,
                        timestamp: existingMessage.timestamp || new Date(),
                        toolCall: existingMessage.toolCall && message.toolCall
                            ? { ...existingMessage.toolCall, ...message.toolCall } // Merge toolCall to preserve params
                            : message.toolCall || existingMessage.toolCall
                    };
                    return { ...prev, messages: updatedMessages };
                }
            }
            // Add new message
            return {
                ...prev,
                messages: [...prev.messages, { ...message, timestamp: new Date() }],
            };
        });
    }, []);
    const replaceMessages = useCallback((messages) => {
        setState((prev) => ({
            ...prev,
            messages: messages.map((message) => ({
                ...message,
                timestamp: message.timestamp ? new Date(message.timestamp) : new Date(),
            })),
        }));
    }, []);
    const updateLastMessageComponents = useCallback((components) => {
        setState((prev) => {
            const messages = [...prev.messages];
            if (messages.length > 0) {
                messages[messages.length - 1] = {
                    ...messages[messages.length - 1],
                    components,
                };
            }
            return { ...prev, messages };
        });
    }, []);
    const updateMessageComponentsByIndex = useCallback((messageIndex, components) => {
        setState((prev) => {
            const messages = [...prev.messages];
            if (messageIndex >= 0 && messageIndex < messages.length) {
                messages[messageIndex] = {
                    ...messages[messageIndex],
                    components,
                };
            }
            return { ...prev, messages };
        });
    }, []);
    const setLoading = useCallback((loading) => {
        setState((prev) => ({ ...prev, isLoading: loading }));
    }, []);
    const setError = useCallback((error) => {
        setState((prev) => ({ ...prev, error }));
    }, []);
    const clearConversation = useCallback(() => {
        setState({
            conversationId: null,
            messages: [],
            isLoading: false,
            error: null,
        });
    }, []);
    const stopExecution = useCallback(() => {
        console.log('[ConversationContext] Stop execution requested');
        setLoading(false);
        // The actual abort will be handled by useSendMessage
    }, []);
    // Auto-save UI state when messages change
    useEffect(() => {
        if (state.conversationId && state.messages.length > 0) {
            // Debounce the save to avoid too frequent writes
            const timeoutId = setTimeout(() => {
                saveConversationUI(state.conversationId, state.messages);
            }, 500);
            return () => clearTimeout(timeoutId);
        }
        return undefined; // Return undefined when condition is not met
    }, [state.conversationId, state.messages]);
    const value = {
        ...state,
        setConversationId,
        addMessage,
        replaceMessages,
        updateLastMessageComponents,
        updateMessageComponentsByIndex,
        setLoading,
        setError,
        clearConversation,
        stopExecution,
    };
    return _jsx(ConversationContext.Provider, { value: value, children: children });
};
export const useConversation = () => {
    const context = useContext(ConversationContext);
    if (!context) {
        throw new Error("useConversation must be used within a ConversationProvider");
    }
    return context;
};
